import React, { useEffect, useState } from "react";
import api, { STAGE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, FolderKanban, CheckCircle2, Clock, AlertTriangle, Users, FileText, Bell, Mail, IndianRupee, DollarSign, ArrowUp, ArrowDown, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import SurveydraftsActivityPanel from "@/components/dashboard/SurveydraftsActivityPanel";
import EmployeeDashboard from "@/pages/EmployeeDashboard";

export function JobsByPeriodSection() {
  const [data, setData] = useState(null);
  const [openRow, setOpenRow] = useState("all"); // which row's per-client list is expanded

  useEffect(() => {
    api.get("/stats/jobs-by-period").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;

  const rows = [
    { key: "all", label: "ALL JOBS", accent: "bg-zinc-200", bucket: data.all },
    { key: "today", label: "TODAY'S JOBS", accent: "bg-yellow-200", bucket: data.today },
    { key: "yesterday", label: "YESTERDAY'S JOBS", accent: "bg-orange-200", bucket: data.yesterday },
  ];

  return (
    <div className="space-y-3" data-testid="jobs-by-period-section">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="label-mono">JOB BUCKETS</div>
          <h2 className="font-heading text-2xl font-black">Pipeline at a glance</h2>
        </div>
        <span className="text-[10px] font-mono text-zinc-500 uppercase">
          {data.scope === "admin" ? "Showing all org jobs" : "Showing your assigned clients only"}
        </span>
      </div>

      {rows.map((r) => (
        <div key={r.key} className="brut-card overflow-hidden" data-testid={`period-row-${r.key}`}>
          <button
            onClick={() => setOpenRow(openRow === r.key ? null : r.key)}
            className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-zinc-50"
            data-testid={`period-toggle-${r.key}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-8 h-8 border border-zinc-900 flex items-center justify-center shrink-0 ${r.accent}`}>
                <FolderKanban className="w-4 h-4" strokeWidth={2.25} />
              </div>
              <div className="min-w-0">
                <div className="label-mono">{r.label}</div>
                <div className="font-heading text-2xl font-black leading-none mt-0.5">{r.bucket.total}</div>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-4 text-xs font-mono">
              <span className="flex items-center gap-1 text-blue-700"><Clock className="w-3.5 h-3.5" /> {r.bucket.in_progress} ip</span>
              <span className="flex items-center gap-1 text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> {r.bucket.completed} done</span>
              <span className="flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> {r.bucket.hold} hold</span>
              <span className="text-zinc-500 ml-2">{r.bucket.by_client.length} clients</span>
              <span className="text-zinc-500 text-base">{openRow === r.key ? "▾" : "▸"}</span>
            </div>
          </button>
          {openRow === r.key && r.bucket.by_client.length > 0 && (
            <div className="border-t border-zinc-900 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900 text-white">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono">Client</th>
                    <th className="text-right px-3 py-2 font-mono">Total</th>
                    <th className="text-right px-3 py-2 font-mono">In Progress</th>
                    <th className="text-right px-3 py-2 font-mono">Completed</th>
                    <th className="text-right px-3 py-2 font-mono">Sent</th>
                    <th className="text-right px-3 py-2 font-mono">On Hold</th>
                  </tr>
                </thead>
                <tbody>
                  {r.bucket.by_client.map((c, idx) => (
                    <tr key={(c.client_id || "noid") + idx} className="border-b border-zinc-200 hover:bg-zinc-50">
                      <td className="px-3 py-1.5 font-medium">{c.client_name || "(no client)"}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-bold">{c.count}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{c.in_progress || ""}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{c.completed || ""}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{c.sent_to_client || ""}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{c.hold || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {openRow === r.key && r.bucket.by_client.length === 0 && (
            <div className="border-t border-zinc-900 px-4 py-6 text-center text-xs font-mono text-zinc-500">
              NO JOBS IN THIS BUCKET
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

